// src/support/SupportTab.js
import React, { useState, useCallback } from 'react';
import useSupportTickets from './useSupportTickets';
import './SupportTab.css';

const CATEGORIES = [
  { value: 'refund', label: 'Refund' },
  { value: 'technical', label: 'Technical Issue' },
  { value: 'event_inquiry', label: 'Event Inquiry' },
  { value: 'account', label: 'Account' },
  { value: 'other', label: 'Other' },
];

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function StatusBadge({ status }) {
  const label = (status || '').replace('_', ' ');
  return <span className={`status-badge ${status}`}>{label}</span>;
}

// ── Create Ticket Modal ──
function CreateTicketModal({ onClose, onCreate }) {
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('normal');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject.trim() || !category || !description.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      await onCreate({ subject: subject.trim(), category, description: description.trim(), priority, email: email.trim() || undefined });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="support-modal-overlay" onClick={onClose}>
      <div className="support-modal" onClick={(e) => e.stopPropagation()}>
        <button className="support-modal-close" onClick={onClose}>✕</button>
        <h2>New Support Ticket</h2>

        {error && <p style={{ color: '#ef4444', fontSize: '0.85rem' }}>{error}</p>}

        <form onSubmit={handleSubmit}>
          <div className="support-field">
            <label>Subject *</label>
            <input className="support-input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Brief summary of your issue" maxLength={200} required />
          </div>

          <div className="support-field">
            <label>Category *</label>
            <select className="support-select" value={category} onChange={(e) => setCategory(e.target.value)} required>
              <option value="">Select category...</option>
              {CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.label}</option>
              ))}
            </select>
          </div>

          <div className="support-field">
            <label>Description *</label>
            <textarea className="support-textarea" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe your issue in detail..." required />
          </div>

          <div className="support-field">
            <label>Priority</label>
            <select className="support-select" value={priority} onChange={(e) => setPriority(e.target.value)}>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div className="support-field">
            <label>Email <small>(optional, for updates)</small></label>
            <input className="support-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com" />
          </div>

          <button className="btn-submit-ticket" type="submit" disabled={submitting || !subject.trim() || !category || !description.trim()}>
            {submitting ? 'Submitting...' : 'Submit Ticket'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ── Ticket Detail View ──
function TicketDetail({ ticketId, account, getDetail, reply, onBack }) {
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getDetail(ticketId);
      setTicket(data.ticket);
      setComments(data.comments || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [ticketId, getDetail]);

  React.useEffect(() => { load(); }, [load]);

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await reply(ticketId, replyText.trim());
      setReplyText('');
      await load();
    } catch {
      // ignore
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div className="support-loading">Loading ticket...</div>;
  if (!ticket) return <div className="support-loading">Ticket not found.</div>;

  return (
    <div className="support-detail">
      <button className="support-detail-back" onClick={onBack}>&larr; Back to tickets</button>

      <div className="detail-header">
        <h3>{ticket.subject}</h3>
        <div className="detail-badges">
          <StatusBadge status={ticket.status} />
          {(ticket.priority === 'urgent' || ticket.priority === 'high') && (
            <span className={`priority-label ${ticket.priority}`}>{ticket.priority}</span>
          )}
          <span className="ticket-category">{ticket.category?.replace('_', ' ')}</span>
        </div>
        <div className="detail-description">{ticket.description}</div>
        <div className="detail-meta">
          <span>Created {timeAgo(ticket.createdAt)}</span>
          {ticket.resolvedAt && <span>Resolved {timeAgo(ticket.resolvedAt)}</span>}
        </div>
      </div>

      <div className="comments-section">
        <h4>Conversation</h4>
        {comments.length === 0 ? (
          <p className="no-comments">No replies yet. Our team will respond shortly.</p>
        ) : (
          <div className="comment-thread">
            {comments.map((c) => (
              <div key={c.id} className={`comment-bubble ${c.authorType}`}>
                <div className="comment-author">
                  {c.authorType === 'admin' ? 'Support Team' : 'You'}
                </div>
                <div className="comment-text">{c.comment}</div>
                <div className="comment-time">{timeAgo(c.createdAt)}</div>
              </div>
            ))}
          </div>
        )}

        {ticket.status !== 'closed' && (
          <div className="reply-form">
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type your reply..."
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); }
              }}
            />
            <button className="btn-send-reply" onClick={handleReply} disabled={sending || !replyText.trim()}>
              {sending ? '...' : 'Send'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main SupportTab ──
export default function SupportTab({ account }) {
  const { tickets, loading, createTicket: create, getDetail, reply, refresh } = useSupportTickets(account);
  const [view, setView] = useState('list'); // 'list' | 'detail'
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const filteredTickets = statusFilter
    ? tickets.filter((t) => t.status === statusFilter)
    : tickets;

  const openDetail = (id) => {
    setSelectedTicketId(id);
    setView('detail');
  };

  const backToList = () => {
    setView('list');
    setSelectedTicketId(null);
    refresh();
  };

  if (!account) {
    return (
      <div className="support-tab">
        <div className="support-empty">
          <div className="support-empty-icon">🔒</div>
          <h3>Connect Wallet</h3>
          <p>Connect your wallet to access support.</p>
        </div>
      </div>
    );
  }

  if (view === 'detail' && selectedTicketId) {
    return (
      <div className="support-tab" role="tabpanel" id="panel-support" aria-labelledby="tab-support">
        <TicketDetail
          ticketId={selectedTicketId}
          account={account}
          getDetail={getDetail}
          reply={reply}
          onBack={backToList}
        />
      </div>
    );
  }

  return (
    <div className="support-tab" role="tabpanel" id="panel-support" aria-labelledby="tab-support">
      <div className="support-header">
        <h2>Support Tickets</h2>
        <button className="btn-new-ticket" onClick={() => setShowCreate(true)}>+ New Ticket</button>
      </div>

      <div className="support-filters">
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            className={`filter-btn ${statusFilter === f.value ? 'active' : ''}`}
            onClick={() => setStatusFilter(f.value)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="support-loading">Loading tickets...</div>
      ) : filteredTickets.length === 0 ? (
        <div className="support-empty">
          <div className="support-empty-icon">📋</div>
          <h3>{statusFilter ? 'No tickets match this filter' : 'No Support Tickets'}</h3>
          <p>{statusFilter ? 'Try a different filter.' : 'Need help? Create a support ticket and our team will assist you.'}</p>
          {!statusFilter && (
            <button className="btn-new-ticket" onClick={() => setShowCreate(true)}>Create Your First Ticket</button>
          )}
        </div>
      ) : (
        <div className="ticket-list">
          {filteredTickets.map((t) => (
            <div
              key={t.id}
              className={`ticket-item ${t.priority === 'urgent' ? 'priority-urgent' : ''} ${t.priority === 'high' ? 'priority-high' : ''}`}
              onClick={() => openDetail(t.id)}
            >
              <div className="ticket-item-header">
                <span className="ticket-subject">{t.subject}</span>
                <StatusBadge status={t.status} />
              </div>
              <div className="ticket-item-meta">
                <span className="ticket-category">{t.category?.replace('_', ' ')}</span>
                {(t.priority === 'urgent' || t.priority === 'high') && (
                  <span className={`priority-label ${t.priority}`}>{t.priority}</span>
                )}
                <span className="ticket-date">{timeAgo(t.createdAt)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateTicketModal
          onClose={() => setShowCreate(false)}
          onCreate={create}
        />
      )}
    </div>
  );
}
